import { Field, FormikErrors, FormikTouched, useFormikContext } from "formik";
import { useEffect, useState } from "react";
import ReactSelect from "react-select";
import { createFilter } from "react-select";
import { Col } from "reactstrap";
import { useGetDCompaniesAsUnits } from "src/pages/Configuration/UnitConfiguration/DCompanies/api/getDCompaniesAsUnits";
import { useGetDepartmentsAsUnits } from "src/pages/Configuration/UnitConfiguration/Departments/api/getDepartmentsAsUnits";
import { useGetEstablishmentsAsUnits } from "src/pages/Configuration/UnitConfiguration/Establishments/api/getEstablishmentsAsUnits";
import { useGetSMarketsAsUnits } from "src/pages/Configuration/UnitConfiguration/SMarkets/api/getSMarketsAsUnits";
import { useGetUnitsAsUnits } from "src/pages/Configuration/UnitConfiguration/Units/api/getUnitsAsUnits";

const UnitsSelectionDropdown: React.FC<UnitsSelectionDropdownProps> = ({
  errors,
  touched,
  unitType,
  field = "unitID",
  unitTypeLabel = "Είδος Μονάδας",
  unitLabel = "Μονάδα",
  requiredAsterisk = false,
  xsSize = 3,
  initialUnitID,
  isFormResetting
}) => {
  const [selectedUnitType, setSelectedUnitType] = useState(unitType || "");
  const [selectedOption, setSelectedOption] = useState<{ value: string, label: string } | null>(null);
  const { setFieldValue } = useFormikContext();
  const getDCompaniesAsUnitsQuery = useGetDCompaniesAsUnits({});
  const getDepartmentsAsUnitsQuery = useGetDepartmentsAsUnits({});
  const getEstablishmentsAsUnitsQuery = useGetEstablishmentsAsUnits({});
  const getUnitsAsUnitsQuery = useGetUnitsAsUnits({});
  const getSMarketsAsUnitsQuery = useGetSMarketsAsUnits({});
  const asterisk = requiredAsterisk ? '*' : '';

  useEffect(() => {
    setSelectedUnitType(unitType || "");

    const allUnits = [
      ...(getDCompaniesAsUnitsQuery.data || []),
      ...(getDepartmentsAsUnitsQuery.data || []),
      ...(getEstablishmentsAsUnitsQuery.data || []),
      ...(getUnitsAsUnitsQuery.data || []),
      ...(getSMarketsAsUnitsQuery.data || [])
    ];
    const initialUnit = allUnits.find(unit => unit.id.toString() === initialUnitID);
    if (initialUnit) {
      setSelectedOption({ value: initialUnit.id.toString(), label: initialUnit.label });
    } else {
      setSelectedOption(null); 
    }

    if (isFormResetting) {
      setSelectedOption(null); 
    }
  }, [unitType, initialUnitID, getDCompaniesAsUnitsQuery.data, getDepartmentsAsUnitsQuery.data, getEstablishmentsAsUnitsQuery.data, getUnitsAsUnitsQuery.data, getSMarketsAsUnitsQuery.data, isFormResetting]);


  const unitlab: { value: string, label: string }[] = [
    { value: '', label: 'Επιλέξτε είδος μονάδας' },
    { value: 'Establishments', label: 'Καταστήματα' },
    { value: 'Departments', label: 'Διευθύνσεις' },
    { value: 'DCompanies', label: 'Θυγατρικές' },
    { value: 'Units', label: 'Μονάδες' },
    { value: 'SMarkets', label:'Σούπερ Μάρκετς'}
  ];

  const handleUnitTypeChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedUnitType(event.target.value);
    setFieldValue('unitType', event.target.value); 

    if (event.target.value === '') {
      setFieldValue('unitID', ''); 
      setSelectedOption(null);
    } else {
      setSelectedOption(null);
    }
  };

  const handleUnitIdChange = (selectedOption: any) => {
    const selectedValue = selectedOption ? selectedOption.value : '';
    setFieldValue(field, selectedValue);
    setSelectedOption(selectedOption);
  };

  const getOptionsForUnitType = (type: string) => {
    switch (type) {
      case 'Establishments':
        return (getEstablishmentsAsUnitsQuery.data || []).map((establishment: { id: number, label: string }) => ({ value: establishment.id.toString(), label: establishment.label }));
      case 'Departments':
        return (getDepartmentsAsUnitsQuery.data || []).map((department: { id: number, label: string }) => ({ value: department.id.toString(), label: department.label }));
      case 'DCompanies':
        return (getDCompaniesAsUnitsQuery.data || []).map((dCompany: { id: number, label: string }) => ({ value: dCompany.id.toString(), label: dCompany.label }));
      case 'Units':
        return (getUnitsAsUnitsQuery.data || []).map((unit: { id: number, label: string }) => ({ value: unit.id.toString(), label: unit.label }));
      case 'SMarkets':
        return (getSMarketsAsUnitsQuery.data || []).map((unit: { id: number, label: string }) => ({ value: unit.id.toString(), label: unit.label }));
      default:
        return [];
    }
  };

  return (
    <>
      <Col xs={xsSize}>
        <div className="flex-grow-1">
          <label className="form-label">{unitTypeLabel}</label>
        </div>
        <div className="mb-3">
          <Field
            as="select"
            name="unit"
            className="form-control"
            value={selectedUnitType}
            onChange={handleUnitTypeChange}
          >
            {unitlab.map((unit) => (
              <option key={unit.value} value={unit.value}>
                {unit.label}
              </option>
            ))}
          </Field>
        </div>
      </Col>

      <Col xs={xsSize}>
        <label className="form-label">{`${unitLabel}${asterisk}`}</label>
        <ReactSelect
          name={field}
          placeholder=""
          value={selectedOption}
          options={getOptionsForUnitType(selectedUnitType)}
          onChange={handleUnitIdChange}
          filterOption={createFilter({
            matchFrom: 'any',
            stringify: (option: reactSelectOption) => `${option.label}`,
          })}
        />
        <div className="text-danger">
          {(errors as { [key: string]: string })[field] && touched[field] && (
            <div>{(errors as { [key: string]: string })[field]}</div>
          )}
        </div>
      </Col>
    </>
  );
};

export default UnitsSelectionDropdown;

interface UnitsSelectionDropdownProps {
  errors: FormikErrors<any>;
  touched: FormikTouched<any>;
  unitType?: string | undefined;
  field?: string;
  unitTypeLabel?: string;
  unitLabel?: string;
  requiredAsterisk?: boolean;
  xsSize?: number;
  initialUnitID?: string;
  isFormResetting?: boolean;
}

type reactSelectOption = {
  value: string;
  label: string;
}